import { Form, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import API from 'helpers/API';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import ReclaimForm from './ReclaimForm';

const matchesFuzzy = (source, query) => {
  const normalizedSource = String(source ?? '').trim().toLowerCase();
  const normalizedQuery = String(query ?? '').trim().toLowerCase();

  if (!normalizedQuery) return true;
  if (normalizedSource.includes(normalizedQuery)) return true;

  let cursor = 0;
  for (const char of normalizedQuery) {
    cursor = normalizedSource.indexOf(char, cursor);
    if (cursor === -1) return false;
    cursor += 1;
  }

  return true;
};

const buildOptions = (records, key, query) => {
  const seen = new Set();

  return (records ?? [])
    .map((record) => record?.[key])
    .filter((value) => {
      const normalized = String(value ?? '').trim();
      if (!normalized) return false;
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return matchesFuzzy(normalized, query);
    })
    .slice(0, 8)
    .map((value) => ({
      label: value,
      value,
    }));
};

const Page = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [locations, setLocations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reclaims, setReclaims] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoadingForm, setIsLoadingForm] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerms, setSearchTerms] = useState({
    customerCompany: '',
    customerName: '',
  });

  const resolveCustomerPhone = (matchedRecord) => {
    if (matchedRecord?.customerPhone) {
      return matchedRecord.customerPhone;
    }

    const customerTel = (customers ?? []).find(
      (customer) =>
        String(customer?.name ?? '').trim() ===
        String(matchedRecord?.customerCompany ?? '').trim(),
    )?.tel;

    if (customerTel) {
      return customerTel;
    }

    const userPhone =
      (users ?? []).find(
        (user) =>
          String(user?.name ?? '').trim() ===
            String(matchedRecord?.customerName ?? '').trim() &&
          String(user?.customer?.name ?? '').trim() ===
            String(matchedRecord?.customerCompany ?? '').trim(),
      )?.phone ??
      (users ?? []).find(
        (user) =>
          String(user?.name ?? '').trim() ===
          String(matchedRecord?.customerName ?? '').trim(),
      )?.phone;

    return userPhone ?? undefined;
  };

  const resolveReclaimLocationRid = (fieldName, selectedValue, matchedRecord) => {
    if (matchedRecord?.reclaimLocationRid) {
      return matchedRecord.reclaimLocationRid;
    }

    const normalizedCompany = String(matchedRecord?.customerCompany ?? '').trim();
    const normalizedName = String(matchedRecord?.customerName ?? '').trim();

    const exactCustomerMatch = (reclaims ?? []).find(
      (reclaim) =>
        String(reclaim?.customerCompany ?? '').trim() === normalizedCompany &&
        String(reclaim?.customerName ?? '').trim() === normalizedName,
    )?.reclaimLocationRid;

    if (exactCustomerMatch) {
      return exactCustomerMatch;
    }

    const sameFieldMatch = (reclaims ?? []).find(
      (reclaim) =>
        String(reclaim?.[fieldName] ?? '').trim() === String(selectedValue ?? '').trim(),
    )?.reclaimLocationRid;

    if (sameFieldMatch) {
      return sameFieldMatch;
    }

    const sameCompanyMatch = normalizedCompany
      ? (reclaims ?? []).find(
          (reclaim) =>
            String(reclaim?.customerCompany ?? '').trim() === normalizedCompany,
        )?.reclaimLocationRid
      : undefined;

    if (sameCompanyMatch) {
      return sameCompanyMatch;
    }

    const sameNameMatch = normalizedName
      ? (reclaims ?? []).find(
          (reclaim) => String(reclaim?.customerName ?? '').trim() === normalizedName,
        )?.reclaimLocationRid
      : undefined;

    return sameNameMatch ?? undefined;
  };

  const applyReclaimTemplateByField = (fieldName, rawValue) => {
    const selectedValue = String(rawValue ?? '').trim();
    if (!selectedValue) {
      return;
    }

    const matchedReclaim = (reclaims ?? []).find(
      (reclaim) => String(reclaim?.[fieldName] ?? '').trim() === selectedValue,
    );
    const matchedOrder = (orders ?? []).find(
      (order) => String(order?.[fieldName] ?? '').trim() === selectedValue,
    );
    const matchedRecord = matchedReclaim ?? matchedOrder;

    if (!matchedRecord) {
      return;
    }

    form.setFieldsValue({
      customerCompany: matchedRecord.customerCompany ?? undefined,
      customerName: matchedRecord.customerName ?? undefined,
      customerPhone: resolveCustomerPhone(matchedRecord),
      customerAddress: matchedRecord.customerAddress ?? undefined,
      customerAddressDetail: matchedRecord.customerAddressDetail ?? undefined,
      quantity: matchedRecord.quantity ?? undefined,
      reclaimLocationRid:
        resolveReclaimLocationRid(fieldName, selectedValue, matchedRecord) ?? undefined,
      reclaimDate: dayjs(),
    });

    setSearchTerms({
      customerCompany: matchedRecord.customerCompany ?? '',
      customerName: matchedRecord.customerName ?? '',
    });
  };

  const doSave = async (values) => {
    const data = {
      ...values,
      reclaimDate: values.reclaimDate.format('YYYYMMDD'),
      quantity: Number(values.quantity),
    };

    setIsSubmitting(true);

    const response = await API.post('/reclaims', data);

    if (response?.success) {
      message.success('저장을 완료하였습니다.');
      navigate('/reclaims');
      return;
    }

    message.error(getApiErrorMessage(response, '저장을 실패했습니다.'));
    setIsSubmitting(false);
  };

  useEffect(() => {
    let isMounted = true;

    const loadForm = async () => {
      setIsLoadingForm(true);

      const [
        locationsResponse,
        ordersResponse,
        reclaimsResponse,
        customersResponse,
        usersResponse,
      ] = await Promise.all([
        API.get('/locations'),
        API.get('/orders'),
        API.get('/reclaims'),
        API.get('/customers'),
        API.get('/users'),
      ]);

      if (!isMounted) return;

      if (locationsResponse?.success) {
        setLocations(locationsResponse.data?.locations ?? []);
      }

      if (ordersResponse?.success) {
        setOrders(ordersResponse.data?.orders ?? []);
      }

      if (reclaimsResponse?.success) {
        setReclaims(reclaimsResponse.data?.reclaims ?? []);
      }

      if (customersResponse?.success) {
        setCustomers(customersResponse.data?.customers ?? []);
      }

      if (usersResponse?.success) {
        setUsers(usersResponse.data?.users ?? []);
      }

      if (
        !locationsResponse?.success ||
        !ordersResponse?.success ||
        !reclaimsResponse?.success ||
        !customersResponse?.success ||
        !usersResponse?.success
      ) {
        const errorResponse =
          !locationsResponse?.success
            ? locationsResponse
            : !ordersResponse?.success
              ? ordersResponse
              : !reclaimsResponse?.success
                ? reclaimsResponse
                : !customersResponse?.success
                  ? customersResponse
                  : usersResponse;
        const errorMessage = getApiErrorMessage(
          errorResponse,
          '회수 등록 정보를 불러오지 못했습니다.',
        );
        message.error(errorMessage);
      }

      setIsLoadingForm(false);
    };

    loadForm();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSearch = (key, value) => {
    setSearchTerms((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const autoCompleteOptions = useMemo(
    () => ({
      customerCompany: buildOptions(
        [...(reclaims ?? []), ...(orders ?? [])],
        'customerCompany',
        searchTerms.customerCompany,
      ),
      customerName: buildOptions(
        [...(reclaims ?? []), ...(orders ?? [])],
        'customerName',
        searchTerms.customerName,
      ),
    }),
    [orders, reclaims, searchTerms],
  );

  return (
    <ReclaimForm
      title="신규 등록"
      form={form}
      initialValues={{
        reclaimDate: dayjs(),
      }}
      locations={locations.map((location) => ({
        label: location.name,
        value: location.rid,
      }))}
      customerCompanyFieldType="autocomplete"
      customerCompanyOptions={autoCompleteOptions.customerCompany}
      customerNameFieldType="autocomplete"
      customerNameOptions={autoCompleteOptions.customerName}
      onCustomerCompanySearch={(value) => handleSearch('customerCompany', value)}
      onCustomerNameSearch={(value) => handleSearch('customerName', value)}
      onCustomerCompanyResolved={(value) =>
        applyReclaimTemplateByField('customerCompany', value)
      }
      onCustomerNameResolved={(value) =>
        applyReclaimTemplateByField('customerName', value)
      }
      isLoadingForm={isLoadingForm}
      isSubmitting={isSubmitting}
      onSubmit={doSave}
      onBack={() => navigate(-1)}
    />
  );
};

export default Page;
